import { useState, useEffect } from "react";
import API from "../api/axios";
import Layout from "../components/Layout";
import toast from "react-hot-toast";
import { useTheme } from "../context/ThemeContext";

const PLANS = {
  monthly: [
    {
      id: "starter", name: "Starter", price: 3000, period: "month", color: "#5a8a8a", plan: "starter",
      features: ["Up to 30 products", "Sales recording", "Basic dashboard", "Low stock alerts", "Weekly email reports", "Works offline"],
    },
    {
      id: "basic", name: "Basic", price: 5000, period: "month", color: "#2ec4b6", plan: "basic",
      features: ["Up to 500 products", "Sales recording", "Full dashboard and reports", "Low stock alerts", "CSV export", "Weekly and monthly reports", "Works offline"],
    },
    {
      id: "pro", name: "Pro", price: 12000, period: "month", color: "#f6a809", plan: "pro",
      features: ["Unlimited products", "Everything in Basic", "Team management up to 5 users", "Priority support", "Expense tracking", "Advanced analytics"],
    },
  ],
  yearly: [
    {
      id: "starter_yearly", name: "Starter", price: 28800, period: "year", saving: "Save 20%", color: "#5a8a8a", plan: "starter",
      features: ["Up to 30 products", "Sales recording", "Basic dashboard", "Low stock alerts", "Weekly email reports", "Works offline"],
    },
    {
      id: "basic_yearly", name: "Basic", price: 45000, period: "year", saving: "Save 25%", color: "#2ec4b6", plan: "basic",
      features: ["Up to 500 products", "Sales recording", "Full dashboard and reports", "Low stock alerts", "CSV export", "Weekly and monthly reports", "Works offline"],
    },
    {
      id: "pro_yearly", name: "Pro", price: 108000, period: "year", saving: "Save 25%", color: "#f6a809", plan: "pro",
      features: ["Unlimited products", "Everything in Basic", "Team management up to 5 users", "Priority support", "Expense tracking", "Advanced analytics"],
    },
  ],
};

export default function Subscription() {
  const { theme } = useTheme();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying] = useState(null);
  const [billing, setBilling] = useState("monthly");

  const card = {
    background: theme.cardBg,
    border: `1px solid ${theme.cardBorder}`,
    borderRadius: "16px",
    padding: "32px",
    position: "relative",
    boxShadow: theme.cardShadow,
  };

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await API.get("/subscriptions/trial-status");
        setStatus(res.data);
      } catch {
        toast.error("Failed to load subscription status");
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, []);

  const handleSubscribe = async (planName) => {
    try {
      const planKey = `${planName}_${billing}`;
      const res = await API.post("/subscriptions/initialize", { plan: planKey });
      window.location.href = res.data.authorization_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to initialize payment");
    }
  };

  const currentPlans = PLANS[billing];

  return (
    <Layout>
      <div style={{ marginBottom: "32px" }}>
        <h1 style={{ fontSize: "28px", fontWeight: "700", color: theme.textPrimary, letterSpacing: "-0.5px" }}>
          Subscription
        </h1>
        <p style={{ fontSize: "14px", color: theme.textSecondary, marginTop: "4px" }}>
          Choose a plan that works for your business
        </p>
      </div>

      {!loading && status && (
        <div style={{
          background: theme.cardBg, border: `1px solid ${theme.cardBorder}`,
          borderRadius: "14px", padding: "20px 24px", marginBottom: "32px",
          display: "flex", alignItems: "center", gap: "24px", flexWrap: "wrap",
          boxShadow: theme.cardShadow,
        }}>
          <p style={{ fontSize: "12px", color: theme.textSecondary, textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Current Plan
          </p>
          <p style={{ fontSize: "18px", fontWeight: "700", color: theme.textPrimary, display: "flex", alignItems: "center", gap: "12px" }}>
            {status.plan === "free" ? "Free Trial" : status.plan === "starter" ? "Starter Plan" : status.plan === "basic" ? "Basic Plan" : "Pro Plan"}
            <span style={{
              fontSize: "11px", fontWeight: "600", padding: "4px 10px",
              borderRadius: "20px", textTransform: "capitalize",
              background: status.status === "subscribed" ? "#0d2e1a" : "#0d2e2e",
              color: status.status === "subscribed" ? "#2ecc71" : "#f5a623",
            }}>
              {status.status === "subscribed" ? "active" : status.status}
            </span>
          </p>
        </div>
      )}

      {/* Billing Toggle */}
      <div style={{
        display: "flex", background: theme.cardBg,
        border: `1px solid ${theme.cardBorder}`, borderRadius: "12px",
        padding: "4px", marginBottom: "32px", width: "fit-content",
      }}>
        {["monthly", "yearly"].map((b) => (
          <button key={b} onClick={() => setBilling(b)} style={{
            padding: "10px 24px", borderRadius: "10px", border: "none",
            fontSize: "14px", fontWeight: "600", cursor: "pointer",
            fontFamily: "inherit", display: "flex", alignItems: "center", gap: "8px",
            background: billing === b ? "#2ec4b6" : "none",
            color: billing === b ? "#0a0f0f" : theme.textSecondary,
            transition: "all 0.2s ease",
          }}>
            {b === "monthly" ? "Monthly" : "Yearly"}
            {b === "yearly" && (
              <span style={{ background: "#0d2e1a", color: "#2ecc71", borderRadius: "20px", padding: "2px 8px", fontSize: "11px", fontWeight: "700" }}>
                Save 25%
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Plan Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "24px", marginBottom: "32px" }}>
        {currentPlans.map((plan) => (
          <div key={plan.id} style={{
            ...card,
            border: plan.recommended ? "2px solid #2ec4b6" : `1px solid ${theme.cardBorder}`,
          }}>
            {plan.recommended && (
              <div style={{
                position: "absolute", top: "-12px", left: "50%", transform: "translateX(-50%)",
                background: "linear-gradient(135deg, #2ec4b6, #1a9e92)",
                color: "#0a0f0f", fontSize: "11px", fontWeight: "700",
                padding: "4px 16px", borderRadius: "20px", whiteSpace: "nowrap",
              }}>Most Popular</div>
            )}
            <h2 style={{ fontSize: "18px", fontWeight: "700", color: theme.textPrimary, marginBottom: "16px" }}>
              {plan.name}
            </h2>
            {plan.saving && (
              <span style={{ background: "#0d2e1a", color: "#2ecc71", borderRadius: "20px", padding: "3px 10px", fontSize: "11px", fontWeight: "700", marginBottom: "12px", display: "inline-block" }}>
                {plan.saving}
              </span>
            )}
            <div style={{ display: "flex", alignItems: "baseline", gap: "8px", marginBottom: "24px" }}>
              <span style={{ fontSize: "22px", fontWeight: "700", color: "#2ec4b6", alignSelf: "flex-start", marginTop: "8px" }}>₦</span>
              <span style={{ fontSize: "36px", fontWeight: "700", color: "#2ec4b6", letterSpacing: "-1px" }}>
                {plan.price.toLocaleString()}
              </span>
              <span style={{ fontSize: "14px", color: theme.textSecondary }}>/{plan.period}</span>
            </div>
            <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: "12px", marginBottom: "32px" }}>
              {plan.features.map((f) => (
                <li key={f} style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                  <span style={{ color: "#2ec4b6", fontWeight: "700", flexShrink: 0 }}>✓</span>
                  <span style={{ fontSize: "14px", color: theme.textSecondary }}>{f}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => handleSubscribe(plan.plan)}
              disabled={paying === plan.id}
              style={{
                width: "100%", padding: "14px", borderRadius: "10px",
                fontSize: "15px", fontWeight: "700", cursor: "pointer", fontFamily: "inherit",
                background: plan.recommended ? "linear-gradient(135deg, #2ec4b6, #1a9e92)" : "none",
                border: plan.recommended ? "none" : "1px solid #2ec4b6",
                color: plan.recommended ? "#0a0f0f" : "#2ec4b6",
              }}
            >
              {paying === plan.id ? "Redirecting..." : `Get ${plan.name}`}
            </button>
          </div>
        ))}
      </div>

      {/* Note */}
      <div style={{
        background: theme.cardBg, border: `1px solid ${theme.cardBorder}`,
        borderRadius: "14px", padding: "20px 24px",
        display: "flex", flexDirection: "column", gap: "8px", marginBottom: "32px",
      }}>
        <p style={{ fontSize: "13px", color: theme.textSecondary }}>
          🔒 Payments processed securely by Paystack. Cancel anytime.
        </p>
        <p style={{ fontSize: "13px", color: theme.textSecondary }}>
          Need a custom plan?{" "}
          <a href="mailto:martsenseinfo@gmail.com" style={{ color: "#2ec4b6", textDecoration: "none", fontWeight: "600" }}>
            Contact us
          </a>
        </p>
      </div>

      {/* Comparison Table — now includes Starter */}
      <div style={{ marginBottom: "32px" }}>
        <h2 style={{ fontSize: "20px", fontWeight: "700", color: theme.textPrimary, marginBottom: "20px" }}>
          Plan Comparison
        </h2>
        <div style={{
          background: theme.cardBg, border: `1px solid ${theme.cardBorder}`,
          borderRadius: "14px", overflowX: "auto", boxShadow: theme.cardShadow,
        }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "560px" }}>
            <thead>
              <tr>
                {[
                  { label: "Feature", color: theme.textSecondary },
                  { label: "Free Trial", color: theme.textSecondary },
                  { label: "Starter", color: "#5a8a8a" },
                  { label: "Basic", color: "#2ec4b6" },
                  { label: "Pro", color: "#2ecc71" },
                ].map(({ label, color }) => (
                  <th key={label} style={{
                    padding: "14px 16px", fontSize: "12px", fontWeight: "600",
                    color, textTransform: "uppercase", letterSpacing: "0.5px",
                    borderBottom: `1px solid ${theme.divider}`,
                    textAlign: label === "Feature" ? "left" : "center",
                    background: theme.cardBg,
                  }}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                ["Products", "Unlimited*", "30", "500", "Unlimited"],
                ["Sales Recording", "✓", "✓", "✓", "✓"],
                ["Dashboard + Profit", "✓", "✓", "✓", "✓"],
                ["Low Stock Alerts", "✓", "✓", "✓", "✓"],
                ["CSV Import", "✓", "✓", "✓", "✓"],
                ["Data Export", "—", "—", "✓", "✓"],
                ["Reports & Analytics", "✓", "✓", "✓", "✓"],
                ["Categories", "✓", "✓", "✓", "✓"],
                ["Expense Tracking", "—", "—", "—", "✓"],
                ["Team Members", "1", "1", "1", "Up to 5"],
                ["Priority Support", "—", "—", "—", "✓"],
                ["Duration", "14 days", "Monthly/Yearly", "Monthly/Yearly", "Monthly/Yearly"],
              ].map(([feature, trial, starter, basic, pro], i) => (
                <tr key={feature} style={{
                  background: i % 2 === 0 ? theme.cardBg : theme.inputBg,
                  borderBottom: `1px solid ${theme.divider}`,
                }}>
                  <td style={{ padding: "14px 16px", fontSize: "14px", color: theme.textPrimary }}>{feature}</td>
                  <td style={{ padding: "14px 16px", fontSize: "14px", color: theme.textSecondary, textAlign: "center" }}>{trial}</td>
                  <td style={{ padding: "14px 16px", fontSize: "14px", color: "#5a8a8a", textAlign: "center" }}>{starter}</td>
                  <td style={{ padding: "14px 16px", fontSize: "14px", color: "#2ec4b6", textAlign: "center" }}>{basic}</td>
                  <td style={{ padding: "14px 16px", fontSize: "14px", color: "#2ecc71", textAlign: "center" }}>{pro}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p style={{ fontSize: "12px", color: theme.textSecondary, marginTop: "12px", fontStyle: "italic" }}>
          * During trial period only — 10 products after trial expires without subscription
        </p>
      </div>
    </Layout>
  );
}